import { getToken, onMessage, getMessaging } from "firebase/messaging";
import { getApp } from "firebase/app";
import axios from "axios";
import toast from "react-hot-toast";

class NotificationService {
  constructor() {
    this.messaging = null;
    this.unsubscribeOnMessage = null;
    this.notificationCallbacks = new Set();
    this.fcmToken = null;
  }

  // Initialize Firebase Messaging
  async initialize() {
    try {
      // Get messaging instance when needed (lazy initialization)
      if (!this.messaging) {
        try {
          const app = getApp(); // Get the already initialized Firebase app
          this.messaging = getMessaging(app);
        } catch (error) {
          console.error("Firebase app not initialized yet:", error);
          // Try again after a short delay
          await new Promise((resolve) => setTimeout(resolve, 100));
          const app = getApp();
          this.messaging = getMessaging(app);
        }
      }

      if (!this.messaging) {
        console.error("Firebase Messaging not initialized");
        return false;
      }

      // Register service worker if not already registered
      if ("serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.register(
          "/firebase-messaging-sw.js"
        );
        console.log("Service Worker registered:", registration);
      }

      // Request notification permissions
      const permission = await this.requestPermission();
      if (permission === "granted") {
        await this.registerFCMToken();
        this.setupMessageListener();
      } else {
        console.log("Notification permission denied or not yet granted");
        // Still set up message listener for in-app notifications
        this.setupMessageListener();
      }

      return true;
    } catch (error) {
      console.error("Failed to initialize notifications:", error);
      return false;
    }
  }

  // Request browser notification permissions
  async requestPermission() {
    try {
      const permission = await Notification.requestPermission();
      console.log("Notification permission:", permission);
      return permission;
    } catch (error) {
      console.error("Error requesting notification permission:", error);
      return "denied";
    }
  }

  // Get and register FCM token with backend
  async registerFCMToken() {
    try {
      if (!this.messaging) {
        console.error("Messaging not initialized");
        return null;
      }

      // Get FCM token with VAPID key for web push notifications
      // VAPID key is REQUIRED for web push - this is from Firebase Console > Project Settings > Cloud Messaging > Web Push certificates
      // If you don't have one, generate it in Firebase Console
      const currentToken = await getToken(this.messaging, {
        vapidKey:
          "BEJezBUob2P97duyQauyEegzfgN8WyYV47cfI7-OX8ILH-edCOhVIbOO2N6Do5AstNEiRiOr2TKWfCnQGoT2e5I", // TODO: Replace with your actual VAPID key from Firebase Console
      });

      if (currentToken) {
        console.log("FCM Token:", currentToken);
        this.fcmToken = currentToken;

        // Send token to backend
        await this.sendTokenToBackend(currentToken);

        // Store token in localStorage for persistence
        localStorage.setItem("fcmToken", currentToken);

        return currentToken;
      } else {
        console.log(
          "No registration token available. Request permission to generate one."
        );
        return null;
      }
    } catch (error) {
      console.error("Error getting FCM token:", error);
      console.error(
        "Make sure you have generated a VAPID key in Firebase Console and added it to the code"
      );
      return null;
    }
  }

  // Send FCM token to backend
  async sendTokenToBackend(token) {
    try {
      const authToken = localStorage.getItem("token");
      const userId = JSON.parse(localStorage.getItem("userData"))?.User?.id;

      if (!authToken || !userId) {
        console.log("User not authenticated, skipping token registration");
        return;
      }

      // Get device ID (using a unique browser identifier)
      const deviceId = this.getDeviceId();

      // Get current locale
      const deviceLanguage = localStorage.getItem("i18nextLng") || "en";

      const response = await axios.put(
        "/api/auth/updateFcmToken",
        {
          userId: userId,
          deviceId: deviceId,
          deviceLanguage: deviceLanguage,
          newFcmToken: token,
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      console.log("FCM token registered with backend:", response.data);
    } catch (error) {
      console.error("Error sending FCM token to backend:", error);
      // If the token doesn't exist yet (404 error), it means user needs to login again
      // The token will be created during login
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes("not found")
      ) {
        console.log(
          "FCM token entry not found. It will be created on next login."
        );
      }
    }
  }

  // Generate a unique device ID for the browser
  getDeviceId() {
    let deviceId = localStorage.getItem("deviceId");
    if (!deviceId) {
      deviceId =
        "web_" +
        Math.random().toString(36).substr(2, 9) +
        Date.now().toString(36);
      localStorage.setItem("deviceId", deviceId);
    }
    return deviceId;
  }

  // Setup listener for foreground messages
  setupMessageListener() {
    if (!this.messaging) return;

    this.unsubscribeOnMessage = onMessage(this.messaging, (payload) => {
      console.log("Message received in foreground:", payload);

      // Show toast notification
      this.showToastNotification(payload);

      // Notify all registered callbacks
      this.notificationCallbacks.forEach((callback) => {
        callback(payload);
      });

      // Play notification sound if enabled
      this.playNotificationSound();
    });
  }

  // Show toast notification
  showToastNotification(payload) {
    const title =
      payload.notification?.title || payload.data?.title || "New Notification";
    const body = payload.notification?.body || payload.data?.body || "";
    const icon = payload.notification?.icon || "/logo192.png";

    // Custom toast with action buttons
    toast.custom(
      (t) => (
        <div
          className={`notification-toast ${
            t.visible ? "animate-enter" : "animate-leave"
          }`}
          style={{
            maxWidth: "350px",
            background: "white",
            padding: "16px",
            borderRadius: "8px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            display: "flex",
            alignItems: "start",
            gap: "12px",
          }}
        >
          <img
            src={icon}
            alt=""
            style={{ width: "40px", height: "40px", borderRadius: "50%" }}
          />
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: "bold", marginBottom: "4px" }}>
              {title}
            </div>
            <div style={{ fontSize: "14px", color: "#666" }}>{body}</div>
            <div style={{ marginTop: "8px", display: "flex", gap: "8px" }}>
              <button
                onClick={() => {
                  this.handleNotificationClick(payload);
                  toast.dismiss(t.id);
                }}
                style={{
                  padding: "4px 12px",
                  background: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                View
              </button>
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{
                  padding: "4px 12px",
                  background: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      ),
      {
        duration: 6000,
        position: "top-right",
      }
    );
  }

  // Handle notification click
  handleNotificationClick(payload) {
    // Check if there's a deep link or specific action
    if (payload.data?.deepLink) {
      window.location.href = payload.data.deepLink;
    } else if (payload.data?.type) {
      // Navigate based on notification type
      switch (payload.data.type) {
        case "task":
          window.location.href = "/#/tasks";
          break;
        case "problem":
          window.location.href = "/#/problems";
          break;
        case "inventory":
          window.location.href = "/#/inventory";
          break;
        default:
          window.location.href = "/#/notifications";
      }
    } else {
      // Default to notifications page
      window.location.href = "/#/notifications";
    }
  }

  // Play notification sound
  playNotificationSound() {
    const soundEnabled = localStorage.getItem("notificationSound") !== "false";
    if (soundEnabled) {
      const audio = new Audio("/notification-sound.mp3");
      audio.play().catch((e) => console.log("Error playing sound:", e));
    }
  }

  // Subscribe to notification updates
  subscribe(callback) {
    this.notificationCallbacks.add(callback);
    return () => {
      this.notificationCallbacks.delete(callback);
    };
  }

  // Show browser native notification
  async showBrowserNotification(title, options = {}) {
    if (Notification.permission === "granted") {
      try {
        const notification = new Notification(title, {
          icon: "/logo192.png",
          badge: "/logo192.png",
          ...options,
        });

        notification.onclick = () => {
          window.focus();
          notification.close();
          if (options.data?.clickAction) {
            window.location.href = options.data.clickAction;
          }
        };

        return notification;
      } catch (error) {
        console.error("Error showing browser notification:", error);
      }
    }
  }

  // Fetch notifications from backend
  async fetchNotifications(farmId, authToken, params = {}) {
    try {
      console.log("[NotificationService] fetchNotifications called with:", {
        farmId,
        hasToken: !!authToken,
        tokenLength: authToken?.length,
        params
      });

      // Check if farmId exists
      if (!farmId) {
        console.log("[NotificationService] No farm ID provided, returning empty array");
        return { status: 200, items: [], totalCount: 0 };
      }

      if (!authToken) {
        console.log("[NotificationService] No auth token, returning empty array");
        return { status: 200, items: [], totalCount: 0 };
      }

      const queryParams = new URLSearchParams();
      if (params.spanId) queryParams.append("spanId", params.spanId);
      if (params.status) queryParams.append("status", params.status);
      if (params.fromDate) queryParams.append("fromDate", params.fromDate);
      if (params.toDate) queryParams.append("toDate", params.toDate);

      const url = `/api/alert/findByFarmId/${farmId}${
        queryParams.toString() ? "?" + queryParams.toString() : ""
      }`;

      console.log("[NotificationService] Making API request:", {
        url,
        authToken: authToken.substring(0, 20) + "..."
      });

      const response = await axios.get(url, {
        headers: {
          Authorization: authToken, // WITHOUT "Bearer" prefix - same as other components
        },
      });

      console.log("[NotificationService] API response received:", {
        status: response.status,
        itemsCount: response.data?.items?.length || 0,
        totalCount: response.data?.totalCount
      });

      // Backend returns { status, message, items, totalCount }
      return response.data;
    } catch (error) {
      console.error("[NotificationService] Error fetching notifications:", {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data
      });
      throw error;
    }
  }

  // Update notification status
  async updateNotificationStatus(
    notificationId,
    authToken,
    status,
    resolutionDetails = null
  ) {
    try {
      console.log("[NotificationService] updateNotificationStatus called:", {
        notificationId,
        status,
        hasToken: !!authToken
      });

      const payload = {
        id: notificationId,
        status: status,
        resolvedDate: status === "resolved" ? Date.now() : null,
      };

      // Add resolution details if provided
      if (status === "resolved" && resolutionDetails) {
        payload.resolutionStatus = "resolved";
        payload.resolutionDetails = resolutionDetails;
      }

      const response = await axios.put("/api/alert", payload, {
        headers: {
          Authorization: authToken, // WITHOUT "Bearer" prefix
        },
      });

      console.log("[NotificationService] Notification updated successfully");
      return response.data;
    } catch (error) {
      console.error("[NotificationService] Error updating notification:", {
        message: error.message,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Mark all notifications as read for a farm
  async markAllAsRead(farmId, authToken) {
    try {
      console.log("[NotificationService] markAllAsRead called:", {
        farmId,
        hasToken: !!authToken
      });

      const response = await axios.post(
        `/api/alert/markAllAsRead/${farmId}`,
        {},
        {
          headers: {
            Authorization: authToken, // WITHOUT "Bearer" prefix
          },
        }
      );

      console.log("[NotificationService] All notifications marked as read");
      return response.data;
    } catch (error) {
      console.error("[NotificationService] Error marking all as read:", {
        message: error.message,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Send custom notification (admin feature)
  async sendCustomNotification(tenantId, authToken, title, description, data = {}) {
    try {
      console.log("[NotificationService] sendCustomNotification called:", {
        tenantId,
        hasToken: !!authToken
      });

      const response = await axios.post(
        "/api/notifications/send",
        {
          tenantId,
          title,
          description,
          data,
        },
        {
          headers: {
            Authorization: authToken, // WITHOUT "Bearer" prefix
          },
        }
      );

      console.log("[NotificationService] Custom notification sent successfully");
      return response.data;
    } catch (error) {
      console.error("[NotificationService] Error sending notification:", {
        message: error.message,
        status: error.response?.status
      });
      throw error;
    }
  }

  // Cleanup
  destroy() {
    if (this.unsubscribeOnMessage) {
      this.unsubscribeOnMessage();
    }
    this.notificationCallbacks.clear();
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;
